const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  async connectedCallback() {
    await this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults, fires `edition:consent-default-set`, and optionally loads GTM. */
  setupConsent = async () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
    await this.loadShopifyConsentApi();
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it
   * and skip the async API call entirely.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({
      analytics: consent.analytics === "yes",
      marketing: consent.marketing === "yes",
      preferences: consent.preferences === "yes"
    });
    if (hide) {
      this.hideBanner();
    }
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
class LogoSlider extends HTMLElement {
  numberOfCopies = 3;
  logosContainer = null;
  originalSlide = null;
  scrollTimeout;
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor() {
    super();
  }
  connectedCallback() {
    this.logosContainer = this.querySelector(".logos");
    this.originalSlide = this.querySelector(".logos-slide");
    if (!this.logosContainer || !this.originalSlide) {
      console.error("logosContainer or originalSlide not found.");
      return;
    }
    this.cloneSlides(this.numberOfCopies);
    this.logosContainer.addEventListener("scroll", () => this.debounce(this.handleScroll.bind(this), 100));
  }
  cloneSlides(count, prepend = false) {
    if (!this.logosContainer || !this.originalSlide) return;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const copy = this.originalSlide.cloneNode(true);
      fragment.appendChild(copy);
    }
    if (prepend) {
      this.logosContainer.insertBefore(fragment, this.logosContainer.firstChild);
    } else {
      this.logosContainer.appendChild(fragment);
    }
  }
  handleScroll() {
    if (!this.logosContainer || !this.originalSlide) return;
    const scrollPosition = this.logosContainer.scrollLeft;
    const slideWidth = this.originalSlide.clientWidth;
    if (scrollPosition >= (this.numberOfCopies - 1) * slideWidth) {
      this.cloneSlides(1);
      this.numberOfCopies++;
    }
    if (scrollPosition <= slideWidth) {
      this.cloneSlides(1, true);
      this.logosContainer.scrollLeft += slideWidth;
      this.numberOfCopies++;
    }
  }
  debounce(func, wait) {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = window.setTimeout(func, wait);
  }
}
customElements.define("cookie-banner", CookieBanner);
customElements.define("logo-slider", LogoSlider);
